import React from 'react';
import { Phone, Mail, MapPin, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import type { PageType } from './Header';

type FooterProps = {
  onNavigate: (page: PageType) => void;
};

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const { isDark } = useTheme();

  const quickLinks: { label: string; page: PageType }[] = [
    { label: 'Home', page: 'home' },
    { label: 'About Us', page: 'about' },
    { label: 'Industries', page: 'products' },
    { label: 'Infrastructure', page: 'infrastructure' },
    { label: 'Contact', page: 'contact' },
    { label: 'News', page: 'news' },
  ];

  const handleNavigate = (page: PageType) => {
    onNavigate(page);
    window.scrollTo(0, 0);
  };

  return (
    <footer className={`w-full ${isDark ? 'bg-vmil-navy' : 'bg-white border-t border-gray-200'}`}>
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16">
        <div className="grid gap-8 grid-cols-1 md:grid-cols-2">
          {/* Logo + Company Name + Tagline */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <div className="flex items-center gap-3 mb-3">
              <img
                src="/vmil-logo-transparent.png"
                alt="Velmurugan Industries Logo"
                className="h-14 sm:h-16 w-auto rounded p-1"
              />
              <p className={`text-sm ${isDark ? 'text-white/80' : 'text-vmil-steel'}`}>Velmurugan Industries Pvt Ltd</p>
            </div>
            <p className={`text-xs sm:text-sm leading-relaxed mb-4 ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
              Serving global OEMs across steel, cement, wind, railway and mining industries.
            </p>
            <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
              Built to International Standards | Trusted Since 1979.
            </p>
          </div>

          {/* Quick Links - Right side */}
          <div className="flex flex-col items-center md:items-end text-center md:text-right">
            <h4 className={`font-bold mb-3 text-xs sm:text-sm uppercase tracking-wider ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>
              Quick Links
            </h4>
            <ul className="flex flex-wrap justify-center md:justify-end gap-x-4 gap-y-2">
              {quickLinks.map((link) => (
                <li key={link.page}>
                  <button
                    onClick={() => handleNavigate(link.page)}
                    className={`transition-colors text-xs sm:text-sm flex items-center gap-1 group ${isDark ? 'text-white hover:text-vmil-blue' : 'text-vmil-steel hover:text-vmil-blue'}`}
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Contact Info - Single line */}
      <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
            <a
              href="https://maps.google.com/maps?q=Developed%20Plot%20Estate%2C%20E53%2C54%2C%20Thuvakudi%2C%20Tamil%20Nadu%20620015%2C%20India"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 transition-colors ${isDark ? 'text-white hover:text-vmil-blue' : 'text-vmil-steel hover:text-vmil-blue'}`}
            >
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span>Thuvakudi, Trichy – 620 015, Tamil Nadu, India</span>
            </a>
            <a
              href="tel:+914312500547"
              className={`flex items-center gap-2 transition-colors ${isDark ? 'text-white hover:text-vmil-blue' : 'text-vmil-steel hover:text-vmil-blue'}`}
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              <span>+91 431 2500 547</span>
            </a>
            <a
              href="mailto:contactus@vmil.in"
              className={`flex items-center gap-2 transition-colors ${isDark ? 'text-white hover:text-vmil-blue' : 'text-vmil-steel hover:text-vmil-blue'}`}
            >
              <Mail className="w-4 h-4 flex-shrink-0" />
              <span>contactus@vmil.in</span>
            </a>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <h5 className={`font-semibold text-xs sm:text-sm mb-1 sm:mb-2 ${isDark ? 'text-white/90' : 'text-vmil-blue'}`}>Disclaimer:</h5>
          <p className={`text-[10px] sm:text-xs leading-relaxed ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
            All images and contents appearing on vmil.in are the exclusive property of Velmurugan Industries Pvt Ltd
            and all images shown are for illustration purposes only and may not be an exact representation of the
            product or facility and Velmurugan Industries Pvt Ltd reserves all rights.
          </p>
        </div>
      </div>

      {/* Copyright */}
      <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <p className={`text-[10px] sm:text-xs text-center ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
            © 2026 Velmurugan Industries Pvt Ltd. All Rights Reserved.
          </p>
          <p className={`text-[10px] sm:text-xs text-center mt-1 ${isDark ? 'text-white/70' : 'text-vmil-steel/70'}`}>
            ISO 9001:2015 | ISO 14001:2015 | ISO 45001:2018 | ISO 3834-2 Certified
          </p>
        </div>
      </div>
    </footer>
  );
};
